/**
 * vehicle-selector.js
 * Old Benz Parts — Cascading vehicle filter (metaobject-driven).
 *
 * Architecture:
 *   - Catalogue: a list of Fahrzeug metaobjects rendered as inline
 *     JSON by layout/theme.liquid (id="obp-fahrzeug-catalog"). Each
 *     entry: { gid, anzeigename, baureihe, motorisierung, von, bis }
 *   - Cascade: Baureihe → Motorisierung → Baujahr, all filtered
 *     client-side from the in-memory catalogue. Sub-millisecond.
 *   - Submit: collect all catalogue records matching the chosen
 *     (Baureihe, Motorisierung, Year) tuple and build a filter URL
 *     with their metaobject GIDs:
 *        ?filter.p.m.custom.fahrzeug=<gid1>&filter.p.m.custom.fahrzeug=<gid2>…
 *   - Persistence: the active selection is saved to sessionStorage so
 *     the breadcrumb pill, status bar, and category-link sync work on
 *     every page — no per-page AJAX needed.
 *
 * Defensive: every init function early-returns if its DOM isn't on the
 * page, so loading this script site-wide is safe.
 */

(function () {
  'use strict';

  // ---- Constants ------------------------------------------------------
  var FILTER_PARAM = 'filter.p.m.custom.fahrzeug';
  // Legacy flat filter params we still strip on reset (in case any old
  // bookmarks or external links carry them).
  var LEGACY_PARAMS = [
    'filter.p.m.custom.autoserie',
    'filter.p.m.custom.motorisierung',
    'filter.p.m.custom.baujahrvon.lte',
    'filter.p.m.custom.baujahrbis.gte'
  ];
  var SESSION_KEY = 'obp_vehicle_filter';

  // ---- Catalogue load -------------------------------------------------
  //
  // Two-tier load strategy:
  //   1) SYNC — inline JSON in layout/theme.liquid ("obp-fahrzeug-catalog").
  //      Fast, but capped at 50 entries by Shopify's Liquid metaobject
  //      iteration limit. Powers first paint so dropdowns aren't empty
  //      on cold cache.
  //   2) ASYNC — /pages/fahrzeug-catalog (alt template
  //      templates/page.fahrzeug-catalog.liquid) paginates through all
  //      Fahrzeuge (250/page, follows nextPage). Cached in sessionStorage
  //      for the session (1h TTL). When this resolves and has more data
  //      than the inline block, we swap CATALOGUE and re-populate all
  //      active selector forms — preserving the user's current selection.
  //
  // Fixes bug from office@lx.co.at 2026-09-01: only 50 of 84+ assigned
  // Fahrzeuge appeared in the homepage selector, hiding W169/W245 etc.
  var CATALOGUE = loadInlineCatalogue();

  var FULL_CATALOG_URL = '/pages/fahrzeug-catalog';
  var FULL_CATALOG_CACHE_KEY = 'obp_fahrzeug_full_v1';
  var FULL_CATALOG_CACHE_TTL = 60 * 60 * 1000; // 1h

  // Kick off the async fetch immediately (module init, not DOMContentLoaded)
  // so the network request runs in parallel with page render. When it
  // resolves and beats the inline count, we refresh the dropdowns.
  loadFullCatalogueAsync().then(function (full) {
    if (!full || full.length <= CATALOGUE.length) return;
    CATALOGUE = full;
    refreshAllSelectors();
  }).catch(function () { /* fall back to inline */ });

  function loadInlineCatalogue() {
    try {
      var el = document.getElementById('obp-fahrzeug-catalog');
      if (!el) return [];
      var data = JSON.parse(el.textContent || '[]');
      if (!Array.isArray(data)) return [];
      return processCatalogue(data);
    } catch (e) { return []; }
  }

  function loadFullCatalogueAsync() {
    // Try sessionStorage cache first — avoids the extra network hop on
    // every page navigation within a session.
    try {
      var cached = JSON.parse(sessionStorage.getItem(FULL_CATALOG_CACHE_KEY) || 'null');
      if (cached && cached.t && (Date.now() - cached.t) < FULL_CATALOG_CACHE_TTL) {
        return Promise.resolve(processCatalogue(cached.data));
      }
    } catch (e) {}

    return fetchAllCatalogPages().then(function (raw) {
      // Only cache a non-empty result. Caching [] would block re-fetches
      // for the full TTL if the alt-template endpoint was temporarily
      // unreachable (page not published yet, template not assigned,
      // 404 during initial deploy, etc.).
      if (Array.isArray(raw) && raw.length > 0) {
        try {
          sessionStorage.setItem(FULL_CATALOG_CACHE_KEY, JSON.stringify({ t: Date.now(), data: raw }));
        } catch (e) {}
      }
      return processCatalogue(raw);
    });
  }

  function fetchAllCatalogPages() {
    return new Promise(function (resolve) {
      var out = [], page = 1, guard = 0;
      // We manage the page counter locally rather than trusting a value
      // echoed back by the template — Shopify's paginate on
      // shop.metaobjects.<type>.values in a page-template context does
      // not always advance correctly, and a stuck server value used to
      // spin this loop until the hard-guard fired.
      function next() {
        if (guard++ > 20) return resolve(out); // hard stop
        // Note: no `Accept: application/json` header — Shopify's alt-page
        // template serves as text/html and adding an Accept header can
        // trigger a stripped/negotiated response. We JSON.parse the body
        // manually below regardless of Content-Type.
        fetch(FULL_CATALOG_URL + '?page=' + page, {
          credentials: 'same-origin'
        })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (txt) {
            if (!txt) return resolve(out);
            var j;
            // Alt-template serves as text/html; parse manually.
            try { j = JSON.parse(txt); } catch (e) { return resolve(out); }
            if (j && Array.isArray(j.items)) out = out.concat(j.items);
            if (j && j.hasMore === true) { page += 1; next(); }
            else resolve(out);
          })
          .catch(function () { resolve(out); });
      }
      next();
    });
  }

  function processCatalogue(rawItems) {
    if (!Array.isArray(rawItems)) return [];

    // Normalise field names — alt-template uses baujahrvon/baujahrbis;
    // legacy inline uses von/bis. Downstream cascade code reads von/bis.
    // Also normalises bare numeric GIDs to full "gid://" prefix so
    // intersection against the assigned-GIDs list is exact-match.
    var seen = {};
    var data = rawItems
      .filter(function (r) { return r && r.gid && r.baureihe; })
      .map(function (r) {
        var gid = r.gid;
        if (typeof gid === 'string' && gid.indexOf('gid://') !== 0) {
          gid = 'gid://shopify/Metaobject/' + gid;
        }
        return {
          gid: gid,
          anzeigename: r.anzeigename,
          baureihe: r.baureihe,
          motorisierung: r.motorisierung,
          // Accept either shape. `von`/`bis` is the internal contract.
          von: (r.von != null) ? r.von : (r.baujahrvon != null ? r.baujahrvon : null),
          bis: (r.bis != null) ? r.bis : (r.baujahrbis != null ? r.baujahrbis : null)
        };
      })
      // Dedupe by GID — some data cleanup needed in admin, but until
      // then we don't want duplicated dropdown entries.
      .filter(function (r) {
        if (seen[r.gid]) return false;
        seen[r.gid] = true;
        return true;
      });

    // Intersect with the assigned-GIDs list — only Fahrzeug entries
    // that are actually referenced by at least one product survive.
    // This keeps the cascade dropdowns honest: customers can't pick a
    // Baureihe / Motor / Year combo that leads to zero products.
    // If the assigned list is missing or empty (e.g. on a fresh store
    // with no products linked yet), we fall through and show the full
    // catalogue — better dev experience than an empty selector.
    try {
      var assignedEl = document.getElementById('obp-fahrzeug-assigned');
      if (assignedEl) {
        var assigned = JSON.parse(assignedEl.textContent || '[]');
        if (Array.isArray(assigned) && assigned.length > 0) {
          var assignedSet = {};
          assigned.forEach(function (g) { assignedSet[g] = true; });
          data = data.filter(function (r) { return assignedSet[r.gid]; });
        }
      }
    } catch (e) {}

    return data;
  }

  // Re-fill the Baureihe dropdown on every active vehicle-selector form
  // after the async catalogue arrives. Preserves the user's current
  // selection if still valid; if they had cascaded into motor/year,
  // dispatching a 'change' re-runs cascadeFromBaureihe (bound by initSelector).
  function refreshAllSelectors() {
    document.querySelectorAll('.vehicle-selector__form').forEach(function (form) {
      var serieSel = form.querySelector('[data-field="serie"]');
      if (!serieSel) return;
      var currentValue = serieSel.value;
      fillSelect(serieSel, baureihen(), currentValue);
      if (currentValue && serieSel.value === currentValue) {
        // Re-cascade so motor/year reflect the (possibly richer) new data.
        serieSel.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // ---- Catalogue helpers ---------------------------------------------
  function uniqSorted(arr) {
    var seen = {}, out = [];
    arr.forEach(function (v) { if (v && !seen[v]) { seen[v] = 1; out.push(v); } });
    return out.sort();
  }

  function recordsBy(filter) {
    return CATALOGUE.filter(function (r) {
      if (filter.baureihe && r.baureihe !== filter.baureihe) return false;
      if (filter.motorisierung && r.motorisierung !== filter.motorisierung) return false;
      if (filter.year != null) {
        var y = parseInt(filter.year, 10);
        if (r.von != null && y < r.von) return false;
        if (r.bis != null && y > r.bis) return false;
      }
      return true;
    });
  }

  function baureihen() {
    return uniqSorted(CATALOGUE.map(function (r) { return r.baureihe; }));
  }

  function motorenFor(baureihe) {
    var motors = recordsBy({ baureihe: baureihe })
                 .map(function (r) { return r.motorisierung; });
    var unique = uniqSorted(motors.filter(function (m) {
      return m && m !== 'alle Motorisierungen';
    }));
    // If the catalogue carries an "alle Motorisierungen" shortcut record
    // for this Baureihe, surface it at the top of the dropdown.
    var hasAll = motors.indexOf('alle Motorisierungen') !== -1;
    if (hasAll) unique.unshift('alle Motorisierungen');
    return unique;
  }

  function yearRangeFor(baureihe, motorisierung) {
    var matches = recordsBy({ baureihe: baureihe, motorisierung: motorisierung });
    var minVon = Infinity, maxBis = -Infinity;
    matches.forEach(function (r) {
      if (r.von != null && r.von < minVon) minVon = r.von;
      if (r.bis != null && r.bis > maxBis) maxBis = r.bis;
    });
    if (minVon === Infinity || maxBis === -Infinity) return null;
    var years = [];
    for (var y = minVon; y <= maxBis; y++) years.push(y);
    return years;
  }

  // ---- DOM helpers ----------------------------------------------------
  function fillSelect(sel, values, current) {
    // Preserve placeholder option (the empty-value one)
    var placeholder = sel.querySelector('option[value=""]');
    sel.innerHTML = '';
    if (placeholder) sel.appendChild(placeholder);
    values.forEach(function (v) {
      var opt = document.createElement('option');
      opt.value = String(v);
      opt.textContent = String(v);
      if (current != null && String(v) === String(current)) opt.selected = true;
      sel.appendChild(opt);
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ---- Selector form binding -----------------------------------------
  function initSelector(form) {
    var serieSel = form.querySelector('[data-field="serie"]');
    var motorSel = form.querySelector('[data-field="motor"]');
    var jahrSel  = form.querySelector('[data-field="baujahr"]');
    var resetBtn = form.querySelector('[data-action="reset"]');
    if (!serieSel || !motorSel || !jahrSel) return;

    var collectionUrl = form.dataset.collectionUrl || '/collections/all';

    // 1) Populate Baureihe dropdown
    fillSelect(serieSel, baureihen());
    serieSel.disabled = false;

    // 2) Pre-fill — prefer sessionStorage (records what the user
    //    actually clicked) over URL reverse-lookup. URL fallback only
    //    when arriving via a direct link without a session, e.g. a
    //    bookmarked or shared filter URL.
    var preBaureihe = '', preMotor = '', preYear = '';
    var savedHasState = false;
    try {
      var saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}');
      if (saved && (saved.baureihe || saved.serie)) {
        preBaureihe   = saved.baureihe || saved.serie || '';
        preMotor      = saved.motorisierung || saved.motor || '';
        preYear       = saved.baujahr || '';
        savedHasState = true;
      }
    } catch (e) {}

    if (!savedHasState) {
      var urlParams = new URLSearchParams(window.location.search);
      var urlGids = urlParams.getAll(FILTER_PARAM);
      if (urlGids.length > 0) {
        var matched = CATALOGUE.filter(function (r) { return urlGids.indexOf(r.gid) !== -1; });
        var uBar = uniqSorted(matched.map(function (r) { return r.baureihe; }));
        var uMot = uniqSorted(matched.map(function (r) { return r.motorisierung; }));
        if (uBar.length === 1) preBaureihe = uBar[0];
        if (uMot.length === 1) preMotor    = uMot[0];
      }
    }

    if (preBaureihe) {
      serieSel.value = preBaureihe;
      cascadeFromBaureihe();
      if (preMotor) {
        motorSel.value = preMotor;
        cascadeFromMotor();
        if (preYear) jahrSel.value = String(preYear);
      }
    }

    // 3) Cascade event handlers — also drive the live preview
    //    (category-card dim + breadcrumb-dropdown dim) based on the
    //    current dropdown state, before the user clicks Submit.
    function previewFromCurrent() {
      var preview = previewGidsFromForm(serieSel.value, motorSel.value, jahrSel.value);
      syncCategoryLinks(preview);
      checkCategoryProducts(preview);
    }
    serieSel.addEventListener('change', function () {
      cascadeFromBaureihe();
      previewFromCurrent();
    });
    motorSel.addEventListener('change', function () {
      cascadeFromMotor();
      previewFromCurrent();
    });
    jahrSel.addEventListener('change', previewFromCurrent);

    function cascadeFromBaureihe() {
      var b = serieSel.value;
      if (!b) {
        fillSelect(motorSel, []); motorSel.disabled = true;
        fillSelect(jahrSel, []);  jahrSel.disabled = true;
        return;
      }
      var motors = motorenFor(b);
      fillSelect(motorSel, motors);
      motorSel.disabled = motors.length === 0;
      var years = yearRangeFor(b, '') || [];
      fillSelect(jahrSel, years);
      jahrSel.disabled = years.length === 0;
    }

    function cascadeFromMotor() {
      var b = serieSel.value;
      var m = motorSel.value;
      if (!b) return;
      var years = yearRangeFor(b, m) || [];
      fillSelect(jahrSel, years);
      jahrSel.disabled = years.length === 0;
    }

    // 4) Submit handler — Baureihe only is fine; motor/year optional
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var b = serieSel.value;
      var m = motorSel.value;
      var y = jahrSel.value;
      if (!b) { serieSel.focus(); return; }

      var matching = recordsBy({
        baureihe: b,
        motorisierung: m || null,
        year: y ? parseInt(y, 10) : null
      });
      var gids = matching.map(function (r) { return r.gid; });

      var qs = new URLSearchParams();
      gids.forEach(function (g) { qs.append(FILTER_PARAM, g); });

      var label = b + (m ? ' · ' + m : '') + (y ? ' · ' + y : '');
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({
          baureihe: b,
          motorisierung: m,
          baujahr: y,
          gids: gids,
          label: label,
          collectionUrl: collectionUrl,
          serie: b,
          serieLabel: b,
          motor: m
        }));
      } catch (err) {}

      var dest = collectionUrl.replace(/\/$/, '');
      window.location.href = dest + (qs.toString() ? '?' + qs.toString() : '');
    });

    // 5) Reset
    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        sessionStorage.removeItem(SESSION_KEY);
        var url = new URL(window.location.href);
        url.searchParams.delete(FILTER_PARAM);
        LEGACY_PARAMS.forEach(function (k) { url.searchParams.delete(k); });
        window.location.href = url.toString();
      });
    }
  }

  // ---- Resolve gids from session (default) or explicit override -----
  // Used by both syncCategoryLinks() and checkCategoryProducts(). When
  // called from a dropdown-change handler, the caller passes the
  // *preview* gids derived from the current selector state — so the
  // live category-card dim/highlight reflects what the user is about
  // to submit, before they actually click search.
  function resolveGids(override) {
    if (Array.isArray(override)) return override;
    try {
      var saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}');
      return (saved && saved.gids) || [];
    } catch (e) { return []; }
  }

  // Compute matching catalogue gids from current dropdown state without
  // touching sessionStorage. Returns [] when no Baureihe is selected.
  function previewGidsFromForm(b, m, y) {
    if (!b) return [];
    return recordsBy({
      baureihe: b,
      motorisierung: m || null,
      year: y ? parseInt(y, 10) : null
    }).map(function (r) { return r.gid; });
  }

  // ---- Category-link sync --------------------------------------------
  // When a vehicle filter is active, append the matching gids to every
  // /collections/<x> link on the page so clicking through preserves the
  // filter. The vehicle filter survives across collection navigation.
  function syncCategoryLinks(gidsOverride) {
    try {
      var gids = resolveGids(gidsOverride);
      document.querySelectorAll('a[href*="/collections/"]').forEach(function (a) {
        try {
          var url = new URL(a.href, window.location.origin);
          if (!url.pathname.includes('/collections/')) return;
          // Strip current vehicle params + any legacy ones
          url.searchParams.delete(FILTER_PARAM);
          LEGACY_PARAMS.forEach(function (k) { url.searchParams.delete(k); });
          // Re-apply the active gids
          gids.forEach(function (g) { url.searchParams.append(FILTER_PARAM, g); });
          a.href = url.toString();
        } catch (e) {}
      });
    } catch (e) {}
  }

  // ---- Category-card emptiness check ---------------------------------
  // For each <a data-base-href="/collections/X">…</a> on the page (the
  // category cards rendered by sections/obp-categories.liquid), fetch
  // sections/obp-product-count.liquid scoped to that collection with
  // the active vehicle filter. If the count is 0, mark the card with
  // `.obp-dropdown-item--empty` so CSS dims it / disables the link.
  // Re-runs on every page load (since sessionStorage drives both this
  // and the active filter, the result is consistent across pages).
  function checkCategoryProducts(gidsOverride) {
    try {
      var gids = resolveGids(gidsOverride);
      var cards = document.querySelectorAll('[data-base-href]');
      if (!cards.length) return;

      // No active vehicle filter → restore all cards to enabled state.
      if (!gids.length) {
        cards.forEach(function (el) { el.classList.remove('obp-dropdown-item--empty'); });
        return;
      }

      // Build the filter querystring once.
      var qs = new URLSearchParams();
      gids.forEach(function (g) { qs.append(FILTER_PARAM, g); });

      // Dedupe: one fetch per unique collection URL. Multiple cards
      // pointing at the same collection (rare, but possible) get
      // updated together. Key by full path (preserving locale prefix)
      // so /en/collections/bremsen and /de/collections/bremsen are
      // treated as distinct — Shopify's filter counts are locale-scoped.
      var urlMap = {};
      cards.forEach(function (el) {
        try {
          var u = new URL(el.dataset.baseHref, window.location.origin);
          if (!/\/collections\//.test(u.pathname)) return;
          // Preserve the full pathname (locale-aware) — don't strip
          // and rebuild, since the /en/ prefix would be lost and the
          // request would 404 or fall back to a different template.
          var key = u.pathname.replace(/\/$/, '');
          if (!urlMap[key]) urlMap[key] = [];
          urlMap[key].push(el);
        } catch (e) {}
      });

      Object.keys(urlMap).forEach(function (pathname) {
        var u = new URL(pathname, window.location.origin);
        // Copy the filter gids onto the URL's query params.
        gids.forEach(function (g) {
          u.searchParams.append(FILTER_PARAM, g);
        });
        u.searchParams.set('sections', 'obp-product-count');
        fetch(u.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (json) {
            if (!json) return;
            var html = json['obp-product-count'] || '';
            var match = html.match(/"count"\s*:\s*(\d+)/);
            var count = match ? parseInt(match[1], 10) : 0;
            urlMap[pathname].forEach(function (el) {
              el.classList.toggle('obp-dropdown-item--empty', count === 0);
            });
          })
          .catch(function () {});
      });
    } catch (e) {}
  }

  // ---- Vehicle status bar --------------------------------------------
  // Red bar at the top of every page when a vehicle is selected.
  function initVehicleStatusBar() {
    try {
      var raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return;
      var data = JSON.parse(raw);
      if (!data || !(data.baureihe || data.serie)) return;
      if (document.getElementById('obp-vehicle-bar')) return;

      var label = data.label;
      if (!label) {
        var b = data.baureihe || data.serie || '';
        var m = data.motorisierung || data.motor || '';
        var y = data.baujahr || '';
        label = b + (m ? ' · ' + m : '') + (y ? ' · ' + y : '');
      }

      var bar = document.createElement('div');
      bar.id = 'obp-vehicle-bar';
      bar.innerHTML =
        '<div class="page-width obp-vehicle-bar__inner">' +
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true" style="flex-shrink:0">' +
            '<path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.08 3.11H5.77L6.85 7zM19 17H5v-5h14v5zm-8-2.5c0 .83-.67 1.5-1.5 1.5S8 15.33 8 14.5 8.67 13 9.5 13s1.5.67 1.5 1.5zm5 0c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5.67-1.5 1.5-1.5 1.5.67 1.5 1.5z"/>' +
          '</svg>' +
          '<span>' + escapeHtml(label) + '</span>' +
          '<a href="#" id="obp-vehicle-bar-reset">Auto zurücksetzen</a>' +
        '</div>';

      document.body.insertBefore(bar, document.body.firstChild);
      document.body.classList.add('obp-has-vehicle-bar');

      var syncBarHeight = function () {
        var h = bar.offsetHeight || 0;
        document.documentElement.style.setProperty('--obp-vehicle-bar-height', h + 'px');
      };
      syncBarHeight();
      requestAnimationFrame(syncBarHeight);
      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(syncBarHeight).observe(bar);
      } else {
        window.addEventListener('resize', syncBarHeight, { passive: true });
      }

      document.getElementById('obp-vehicle-bar-reset').addEventListener('click', function (e) {
        e.preventDefault();
        sessionStorage.removeItem(SESSION_KEY);
        var url = new URL(window.location.href);
        url.searchParams.delete(FILTER_PARAM);
        LEGACY_PARAMS.forEach(function (k) { url.searchParams.delete(k); });
        window.location.href = url.toString();
      });
    } catch (e) {}
  }

  // ---- Full vehicle-filter teardown -----------------------------------
  // Clears sessionStorage AND removes any visible side-effects from the
  // current DOM. Used when the user clicks a "clear all" affordance.
  // Necessary because Dawn handles some clear-filter actions via AJAX
  // (URL updates without a full page reload) — without this, the red
  // bar would linger in the DOM until the next hard navigation.
  function clearVehicleFilterEverywhere() {
    sessionStorage.removeItem(SESSION_KEY);
    // Tear down the red status bar.
    var bar = document.getElementById('obp-vehicle-bar');
    if (bar) bar.remove();
    document.body.classList.remove('obp-has-vehicle-bar');
    document.documentElement.style.removeProperty('--obp-vehicle-bar-height');
    // Strip vehicle params from category links so subsequent navigation
    // doesn't re-introduce the filter.
    syncCategoryLinks();
    // Un-dim any greyed category cards / breadcrumb dropdown items.
    checkCategoryProducts();
  }

  // ---- "Clear all filters" interception ------------------------------
  // Shopify's empty-collection-state link ("entferne alle") and the
  // active-facets clear-all link both clear URL filter params but
  // don't know about our vehicle sessionStorage. Catch the click and
  // wipe the vehicle filter too so "remove all" really means all.
  function initClearAllInterception() {
    document.addEventListener('click', function (e) {
      var el = e.target.closest('a, button');
      if (!el) return;
      if (el.closest('.collection--empty') || el.closest('.active-facets')) {
        clearVehicleFilterEverywhere();
      }
    }, true);
  }

  // ---- Hide Fahrzeug pills + active-facets wrapper -------------------
  // CSS-only :has() selectors don't reliably target the active filter
  // pill across Dawn DOM variants (sometimes <facet-remove>, sometimes
  // <a>, sometimes nested form). We do it deterministically in JS:
  // find any element inside .active-facets whose visible text starts
  // with "Fahrzeug", hide it. Then if the wrapper has no remaining
  // visible pills, hide the wrapper too (avoids the empty FILTER: box).
  function hideFahrzeugFacetPills() {
    document.querySelectorAll('.active-facets').forEach(function (wrapper) {
      // Hide any pill whose text content references Fahrzeug.
      // The :scope-prefixed selector covers Dawn's standard pill
      // classes; the loop also catches any other direct child element
      // a Dawn fork might use.
      var pills = wrapper.querySelectorAll(
        '.active-facets__button-wrapper, .active-facets__button, .facets-remove, facet-remove, [class*="active-facets__button"]'
      );
      pills.forEach(function (el) {
        var t = (el.textContent || '').trim().toLowerCase();
        if (t.indexOf('fahrzeug') === 0 || t.indexOf('fahrzeug:') !== -1) {
          el.style.display = 'none';
        }
      });

      // If no visible pills remain, hide the wrapper (and its
      // "FILTER:" heading + "Alle entfernen" link) to avoid an
      // empty box. We check for any pill-like element NOT styled
      // display:none.
      var anyVisible = false;
      wrapper.querySelectorAll(
        '.active-facets__button-wrapper, .active-facets__button, .facets-remove, facet-remove'
      ).forEach(function (el) {
        if (el.style.display !== 'none' && el.offsetParent !== null) {
          anyVisible = true;
        }
      });
      if (!anyVisible) wrapper.style.display = 'none';
    });
  }

  // ---- Boot -----------------------------------------------------------
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.vehicle-selector__form').forEach(initSelector);
    syncCategoryLinks();
    checkCategoryProducts();
    initVehicleStatusBar();
    initClearAllInterception();
    hideFahrzeugFacetPills();
  });
})();
